import { useCallback, useContext } from "react";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import { useIntegrationAccountLinkMutation } from "../../api/integrationAccount.api";
import { Button } from "../general/Button/Button";
import { ImporterContext } from "./importer.context";
import styles from "./ImporterPage.module.scss";
import { ImporterDocumentImportStatus, ImporterDocumentRow } from "./types";

interface Props {
    rowsReadyForImport: ImporterDocumentRow[];
    allDocumentsImported: boolean;
    onHide: () => void;
    onNext?: () => void;
}

export const ImporterPageFooter: React.FC<Props> = ({
    rowsReadyForImport,
    allDocumentsImported,
    onHide,
    onNext,
}) => {
    const { updateDocumentRow } = useContext(ImporterContext);

    const linkAccount = useIntegrationAccountLinkMutation();

    const importNextDocument = useCallback(
        async (rowsForImport: ImporterDocumentRow[]) => {
            const nextRow = rowsForImport.shift();

            if (!nextRow) {
                return;
            }

            if (nextRow.integrationAccount) {
                updateDocumentRow(nextRow.id, {
                    importStatus: ImporterDocumentImportStatus.IN_PROGRESS,
                });
                await linkAccount.mutateAsync(nextRow.integrationAccount);
                updateDocumentRow(nextRow.id, {
                    importStatus: ImporterDocumentImportStatus.FINISHED,
                });
            }

            await importNextDocument(rowsForImport);
        },
        [linkAccount, updateDocumentRow],
    );

    const importDocuments = useCallback(() => {
        rowsReadyForImport.forEach((row) => {
            updateDocumentRow(row.id, {
                importStatus: ImporterDocumentImportStatus.SCHEDULED,
            });
        });

        importNextDocument(rowsReadyForImport);
    }, [importNextDocument, rowsReadyForImport, updateDocumentRow]);

    if (rowsReadyForImport.length) {
        if (onNext) {
            return (
                <div className={styles.footer}>
                    <Button onClick={onNext}>Continue</Button>
                </div>
            );
        }

        return (
            <div className={styles.footer}>
                <ButtonWithLoader
                    onClick={importDocuments}
                    loading={linkAccount.isPending}
                >
                    Import documents{" "}
                    <span className={styles.importDocumentsNumber}>
                        {rowsReadyForImport.length}
                    </span>
                </ButtonWithLoader>
            </div>
        );
    }

    if (allDocumentsImported) {
        return (
            <div className={styles.footer}>
                <Button onClick={onHide}>Close</Button>
            </div>
        );
    }

    return null;
};
